import { Directive, ElementRef, Input, OnChanges } from '@angular/core';

import { DecorTextureService } from '~shared/services/decor-texture.service';

interface BoardLike {
	imageUrl?: string;
	externalLabel?: string;
}

/**
 * Shows a board's swatch image on the host, falling back to the decor colour
 * map (assets/media/decor/<externalLabel>/color.jpg) when the board's own
 * imageUrl can't be fetched or is missing. Works on <img> (sets `src`) and on
 * any other element (sets `background-image`).
 */
@Directive({
	selector: '[appBoardImage]',
})
export class BoardImageDirective implements OnChanges {
	@Input('appBoardImage') board: BoardLike | null | undefined;

	constructor(private el: ElementRef<HTMLElement>, private decor: DecorTextureService) {}

	ngOnChanges(): void {
		const url = this.board?.imageUrl;
		const fallback = this.decor.getColorMapUrl(this.board?.externalLabel);
		if (url) {
			this.tryLoad(url, () =>
				fallback ? this.tryLoad(fallback, () => this.apply(null)) : this.apply(null)
			);
		} else if (fallback) {
			this.tryLoad(fallback, () => this.apply(null));
		} else {
			this.apply(null);
		}
	}

	private tryLoad(src: string, onFail: () => void): void {
		const probe = new Image();
		probe.onload = () => this.apply(src);
		probe.onerror = () => onFail();
		probe.src = src;
	}

	private apply(src: string | null): void {
		const el = this.el.nativeElement;
		if (el instanceof HTMLImageElement) {
			if (src) {
				el.src = src;
				el.style.removeProperty('display');
			} else {
				el.style.display = 'none';
			}
		} else {
			el.style.backgroundImage = src ? `url("${src}")` : '';
		}
	}
}
